import React from "react";

import { useChatSidebar } from "@agent-ui/features/chat/hooks/useChatSidebar";
import {
  useEditorStoreActions,
  usePanelView,
} from "@agent-ui/store/editorStore";
import { Button } from "@replo/design-system/components/button/Button";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { History, PanelLeft } from "lucide-react";
import { motion } from "motion/react";

const SHIMMER_DURATION = 2.5;

const AnimatedEllipsis = () => (
  <span className="inline-flex w-[1em] ml-0.5">
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="text-muted"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0, 1, 1, 0, 0] }}
        transition={{
          duration: SHIMMER_DURATION,
          repeat: Number.POSITIVE_INFINITY,
          times: [0, 0.45 + i * 0.08, 0.45 + i * 0.08 + 0.04, 0.85, 0.92, 1],
        }}
      >
        .
      </motion.span>
    ))}
  </span>
);

const ChatTitle = ({
  chatTitle,
  isGeneratingTitle,
}: {
  chatTitle?: string | null;
  isGeneratingTitle?: boolean;
}) => {
  if (isGeneratingTitle) {
    return (
      <div className="text-sm font-medium">
        <span className="text-shimmer">Generating title</span>
        <AnimatedEllipsis />
      </div>
    );
  }

  return (
    <div className="text-sm font-semibold line-clamp-1">
      {chatTitle ?? "New Chat"}
    </div>
  );
};

function ChatHeader({
  showTitle = true,
  chatTitle,
  showVersionHistory = true,
  isGeneratingTitle,
}: {
  showTitle?: boolean;
  chatTitle?: string | null;
  showVersionHistory?: boolean;
  isGeneratingTitle?: boolean;
}) {
  const { open: isChatSidebarOpen, toggle: toggleChatSidebar } =
    useChatSidebar();
  const panelView = usePanelView();
  const { setPanelView } = useEditorStoreActions();

  return (
    <div className="w-full flex items-center justify-between">
      <div className="flex flex-row items-center gap-3">
        <Tooltip
          content={isChatSidebarOpen ? "Close chats" : "Show all chats"}
          triggerAsChild
        >
          <Button
            icon
            variant="secondary"
            UNSAFE_className="hidden md:flex"
            onClick={toggleChatSidebar}
          >
            <PanelLeft />
          </Button>
        </Tooltip>

        {showTitle && (
          <ChatTitle
            chatTitle={chatTitle}
            isGeneratingTitle={isGeneratingTitle}
          />
        )}
      </div>

      {showVersionHistory && (
        <Tooltip content="Version History" triggerAsChild>
          <Button
            icon
            variant="tertiary"
            UNSAFE_className={
              panelView === "history"
                ? "bg-sky-soft text-sky border-sky-border"
                : ""
            }
            onClick={() => {
              return setPanelView(panelView === "history" ? "chat" : "history");
            }}
          >
            <History />
          </Button>
        </Tooltip>
      )}
    </div>
  );
}

export default ChatHeader;
